import { request, getApiUrl } from '@/utils/ajax';
import { UserSettingsObject, EmailValidity } from '@/types';
import { UserSalutation, UserTitle } from '@ygtechnologies/bygr-frontend-component-library';

export function getCurrentUserInfo() {
    return request(`api/user/current`).then((x) => x.json());
}

export function approveExternalLogin(approvalToken: string): Promise<any> {
    return request(`api/user/approve-external`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: {
            approvalToken,
        },
    });
}

export function login(email: string, password: string, remember = false, inviteToken: string | null = null): Promise<any> {
    return request(`api/user/login/cookie`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: {
            email,
            password,
            remember,
            inviteToken: inviteToken || '',
        },
    });
}

export function logout() {
    return request(`api/user/logout/cookie`, {
        method: 'POST',
    });
}

export interface RegisterData {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    salutation: UserSalutation | null;
    title: UserTitle | null;
    inviteToken: string | null;
}

export function register(data: RegisterData) {
    return request(`api/user/register/cookie`, {
        method: 'POST',
        json: true,
        body: data,
    });
}

export function getUserSettings(): Promise<UserSettingsObject> {
    return request(`api/user/current/settings`).then((x) => x.json());
}

export function updateUserSettings(
    firstName: string,
    lastName: string,
    phoneNumber,
    title: UserTitle | null,
    salutation: UserSalutation | null
) {
    return request(`api/user/current/settings`, {
        method: 'POST',
        json: true,
        body: {
            firstName,
            lastName,
            phoneNumber,
            title,
            salutation,
            // address
        },
    });
}

export function requestPasswordReset(email) {
    return request(`api/user/request-password-reset`, {
        method: 'POST',
        json: true,
        body: {
            email,
        },
    });
}

export function resetPassword(token, newPassword) {
    return request(`api/user/password-reset`, {
        method: 'POST',
        json: true,
        body: {
            token,
            newPassword,
        },
    });
}

export function getLoginExternalAccountUrl(provider, returnUrl) {
    return (
        getApiUrl() +
        `/api/user/login/external?provider=${encodeURIComponent(provider)}&returnUrl=${encodeURIComponent(returnUrl || '')}`
    );
}

export function getLinkExternalAccountUrl(provider, returnUrl) {
    return (
        getApiUrl() +
        `/api/user/current/link-account?provider=${encodeURIComponent(provider)}&returnUrl=${encodeURIComponent(
            returnUrl || ''
        )}`
    );
}

export enum ConfirmEmailResult {
    Success = 'Success',
    InvalidToken = 'InvalidToken',
    AlreadyConfirmed = 'AlreadyConfirmed',
}

interface ConfirmEmailResponse {
    result: ConfirmEmailResult;
}

export function confirmEmail(token): Promise<ConfirmEmailResponse> {
    return request('api/user/confirm-email', {
        method: 'POST',
        json: true,
        body: { token },
    }).then((x) => x.json());
}
export function changePassword(currentPassword, newPassword) {
    return request('api/user/current/change-password', {
        method: 'POST',
        json: true,
        body: {
            currentPassword,
            newPassword,
        },
    });
}
export function sendConfirmationEmail() {
    return request('api/user/send-confirm-email', {
        method: 'POST',
    });
}
/**
 * @param identification Either email or applicationUserId
 */
export function getUserAvatarUrl(identification: string | number = 'current') {
    return getApiUrl() + `/api/user/${identification}/avatar`;
}
/**
 * @param identification Either email or applicationUserId
 */
export function getUserAvatarThumbnailUrl(identification: number | 'current' = 'current') {
    return getApiUrl() + `/api/user/${identification}/avatar/thumbnail`;
}

export function changeAvatar(fileData) {
    return request('api/user/current/avatar', {
        method: 'POST',
        json: true,
        body: fileData,
    });
}

export function setUserLocale(locale) {
    return request('api/user/current/locale', {
        method: 'PUT',
        json: true,
        body: locale,
    });
}

export function acceptTerms(projectId?: number) {
    return request(`api/user/accept-terms`, {
        json: true,
        body: {
            projectId: projectId ? projectId : null,
        },
        method: 'POST',
    });
}
interface RegisterWithProfileData extends RegisterData {
    companyName: string;
    companyHomepage: string;
}
export function registerWithProfile(data: RegisterWithProfileData) {
    return request('api/user/register-with-profile', {
        method: 'POST',
        body: data,
        json: true,
    });
}

export function newContactRequest(email: string) {
    return request('api/user/contact-request', {
        method: 'POST',
        body: { email },
        json: true,
    });
}

export function logVisit() {
    return request('api/user/log-visit', {
        method: 'POST',
    });
}
export function checkRegisterEmailAddress(email: string): Promise<EmailValidity> {
    return request(`api/user/check-email`, {
        query: { email },
    }).then((x) => x.json());
}
